import { ApiError } from '../../api/apiError';

const AGENCY_ERROR_MESSAGES: Record<string, string> = {
  AGENCY_ALREADY_EXISTS: 'An agency is already registered for this domain. Contact CLIPr support if this is unexpected.',
  SPONSOR_EMAIL_IN_USE: 'An account already exists for this admin email. Sign in or use a different email.',
  WEAK_SPONSOR_PASSWORD: 'That password was rejected. Choose a stronger password and try again.',
  AGENCY_SIGNUP_UNAVAILABLE: 'We could not complete signup right now. Please try again in a few minutes.',
  PUBLIC_EMAIL_DOMAIN: 'Use your agency email address. Public domains like gmail.com are not allowed.',
  SPONSOR_DOMAIN_MISMATCH: 'Admin email must use the same domain as the agency domain.',
  OFFICER_DOMAIN_MISMATCH: 'Officer and supervisor emails must use the agency domain.',
  INVALID_ROSTER_SIZE: 'Roster size does not match the required minimum or exceeds the maximum.',
  INVALID_PASSWORD: 'Password must be at least 8 characters and include a number.',
  TERMS_REQUIRED: 'You must accept the CLIPr Terms and Privacy Policy to continue.',
};

const FALLBACK_MESSAGE = 'Something went wrong. Please review your details and try again. Contact CLIPr support if the problem persists.';
const NETWORK_OFFLINE_MESSAGE = 'You appear to be offline. Reconnect and try again.';

export function resolveAgencySignupError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.type === 'NetworkOffline')
      return NETWORK_OFFLINE_MESSAGE;

    const graphQlErrors = err.data.graphQLError?.errors ?? [];
    for (const graphQlError of graphQlErrors) {
      const mapped = mapKnownErrorType(graphQlError.errorType ?? null);
      if (mapped)
        return mapped;

      const messageFromCode = extractCodeFromMessage(graphQlError.message);
      if (messageFromCode)
        return messageFromCode;

      if (looksLikeReadableMessage(graphQlError.message))
        return graphQlError.message;
    }

    if (looksLikeReadableMessage(err.originalMessage))
      return err.originalMessage!;
  }

  if (err && typeof err === 'object' && 'message' in err && looksLikeReadableMessage((err as { message: unknown }).message))
    return (err as { message: string }).message;

  return FALLBACK_MESSAGE;
}

function mapKnownErrorType(errorType: string | null): string | null {
  if (!errorType)
    return null;

  return AGENCY_ERROR_MESSAGES[errorType] ?? null;
}

function extractCodeFromMessage(message: string | undefined): string | null {
  if (!message)
    return null;

  for (const code of Object.keys(AGENCY_ERROR_MESSAGES)) {
    if (message.includes(code))
      return AGENCY_ERROR_MESSAGES[code];
  }
  return null;
}

function looksLikeReadableMessage(message: unknown): message is string {
  if (typeof message !== 'string' || !message.trim())
    return false;

  if (/^[A-Z][A-Za-z]*Error$/.test(message.trim()))
    return false;

  return true;
}
